import dayjs from 'dayjs';
import {useSelector} from 'react-redux';
import type {RootState} from '../../../modules';
import {useMemo, useState, useEffect} from 'react';
import {getBillingPortalLink} from '../promises';
import {useToastActions} from '../../toast-alert/utils';

const endDateSelector = (state: RootState) =>
  state.tiersList.currentSubscription &&
  state.tiersList.currentSubscription.endDate;

const elapsedDateSelector = (state: RootState) =>
  state.tiersList.currentSubscription &&
  state.tiersList.currentSubscription.startDate;

const currentTierSelector = (state: RootState) =>
  state.tiersList.currentSubscription &&
  state.tiersList.currentSubscription.storeTierPlan.storeTier.name;

const currentSubscriptionSelector = (state: RootState) =>
  state.tiersList && state.tiersList.currentSubscription;

const subscriptionSettingSelector = (state: RootState) =>
  state.accountConfig.subscriptionSetting;

export function useTierDueEndDate() {
  const endDate = useSelector(endDateSelector);

  if (!endDate) {
    return {
      endDate: null,
      daysLeft: null,
    };
  }

  return {
    endDate,
    daysLeft: dayjs(endDate).diff(dayjs(), 'day'),
  };
}

export function useDaysElapsed() {
  const startDate = useSelector(elapsedDateSelector);

  if (!startDate) {
    return {
      startDate: null,
      daysElapsed: null,
    };
  }

  const daysElapsed = dayjs().diff(dayjs(startDate), 'day');

  return {
    startDate,
    daysElapsed,
  };
}

export function useCurrentTier() {
  const currentTier = useSelector(currentTierSelector);

  return currentTier;
}

export function useNextSubscription() {
  const nextSubscription = useSelector(currentSubscriptionSelector);
  return nextSubscription;
}

export const useCurrentSubscription = () => {
  const currentSubscription = useSelector(currentSubscriptionSelector);
  return currentSubscription;
};

export const useSubscriptionSetting = () => {
  const susbscriptionSetting = useSelector(subscriptionSettingSelector);
  return susbscriptionSetting;
};

export const useIsInEndSubscriptionDays = (days: number = 7) => {
  const subscription = useCurrentSubscription();

  const isWithinEndDays = useMemo(() => {
    if (!subscription) {
      return false;
    }

    const today = new Date();
    const subscriptionEndDate = subscription.endDate;

    if (!subscriptionEndDate) {
      return false;
    }

    const startDateToCheck = new Date(subscriptionEndDate);
    startDateToCheck.setDate(startDateToCheck.getDate() - days);

    return today >= startDateToCheck && today <= subscriptionEndDate;
  }, [subscription, days]);

  return isWithinEndDays;
};

export function useBillingPortalLink() {
  const [billingLinks, setBillingLinks] = useState({});
  const subscription = useCurrentSubscription();

  const {showToast} = useToastActions();

  useEffect(() => {
    if (!subscription) return;
    getBillingPortalLink(window.location.href)
      .then(response => {
        const billingLink = response.url;
        const pricingTableLink = `${billingLink}/subscriptions/${subscription?.storeTierPlan?.stripeId}/update`;
        setBillingLinks({
          billingLink,
          pricingTableLink,
        });
      })
      .catch(() => {
        showToast({
          message: 'Ocurrió un error al cargar los planes de TiendaDa',
          title: 'Error',
          type: 'danger',
        });
      });
  }, [showToast, subscription]);

  return billingLinks;
}
